import React, { useState, useEffect } from 'react';
import { Grid, Container } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import {
  BoxV2 as Box,
  Flex,
  Table,
  MultimediaList,
  AttachmentPreview,
  useTable,
} from 'portal-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShareNodes,
  faSquarePollHorizontal,
  faMessageDots,
  faFileImage,
  faCalendarRange,
  faAngleLeft,
  faTag,
  faBullhorn,
} from '@fortawesome/pro-regular-svg-icons';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { Loader } from '@/shared_elements';
import {
  listCampaignElectedDcaApi,
  getNominationStatusApi,
  getCampaignAttachmentsApi,
  downloadAttachmentApi,
  getProfile,
  getCampaignTermsAndConditions,
  getEventHistory,
  downloadAttachmentToDesktop,
  getCampaignOperations,
  fetchCampaignDetails,
} from '../apiServices';
import {
  CampaignDescription,
  CampaignSampleMessage,
  MnoStatusTableNew,
  CampaignAttributeNew,
  ElectCNPAlert,
  NumbersProvisioned,
  DeclinedExplanation,
  OtherResponsiblePartiesSection,
} from '../components';
import PartnerCampaignDetailsBasic from '../components/PartnerCampaignDetailsBasic';
import EventListingRow from '@/components/EventListingRow';
import '../../../../assets/styles/campaign-detail-module.scss';
import BrandDetails from '../components/BrandDetails';
import MessageFlowAttributePopup from '../components/MessageFlowAttributePopup';

import { getMigrationAndProvisionalInfo } from '../utils';

import SupportingDocumentSection from '../components/SupportingDocumentSection';
import EmbeddedLinkPopup from '../components/EmbeddedLinkPopup';
import {
  CNP_CAMPAIGNS_DEFAULT_ENTRY,
  CNP_CAMPAIGNS_PATH,
} from '@/constants/paths';
import { ATTACHMENT_FOLDER_MMS } from '../constants';
import { campaignController, enumController } from '@/apis/core';
import { MAX_EVENT_RECORDS } from '../../Events/constants';
import { getCnpList } from '../apis';

const useStyles = (theme) => ({
  modal: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: 430,
    height: 226,
    background: '#FFFFFF',
  },
});

const headRows = [
  {
    id: 'eventType',
    label: 'EVENT TYPE',
    sortable: false,
  },
  {
    id: 'categoryType',
    label: 'CATEGORY',
    sortable: false,
  },
  {
    id: 'campaignUid',
    label: 'CAMPAIGN ID',
    sortable: false,
  },
  {
    id: 'brandUid',
    label: 'BRAND ID',
    sortable: false,
  },
  {
    id: 'updateDate',
    label: 'DATE & TIME',
    sortable: false,
  },
  {
    id: 'authenticatedUser',
    label: 'USER',
    sortable: false,
  },
  {
    id: 'description',
    label: 'DESCRIPTION',
    sortable: false,
  },
];

const HeadingBlock = ({ icon, title, children, ...props }) => (
  <div className="details-heading" {...props}>
    <Flex sx={{ alignItems: 'center' }}>
      <Box sx={{ mr: 'xs' }}>
        <FontAwesomeIcon icon={icon} size="xl" color="#49535D" />
      </Box>
      <h3 className="heading1">{title}</h3>
    </Flex>
    {children}
  </div>
);

const PartnerCampaignDetailV2 = () => {
  const history = useHistory();
  const location = useLocation();
  const { id: campaignUid } = useParams();

  const [loader, setLoader] = useState(true);
  const [campaign, setCampaign] = useState({});
  const [sharingStatus, setSharingStatus] = useState('');
  const [rejectionCategoryMap, setRejectionCategoryMap] = useState({});
  const [rejectionCategoryIds, setRejectionCategoryIds] = useState([]);
  const [explanation, setExplanation] = useState('');
  const [declinedOnDate, setDeclinedOnDate] = useState(0);
  const [upstreamCnpName, setUpstreamCnpName] = useState('');
  const [sharedToStatus, setSharedToStatus] = useState('');
  const [goBackPage] = useState(
    location.state?.goBackPage ?? CNP_CAMPAIGNS_DEFAULT_ENTRY
  );
  const [electedDcas, setElectedDcas] = useState([]);
  const [openAlert, setOpenAlert] = useState(false);
  const [sampleMultimedia, setSampleMultimedia] = useState([]);
  const [sampleMultimediaIndex, setSampleMultimediaIndex] = useState(-1);
  const [termsAndConditions, setTermsAndConditions] = useState([]);
  const [dpaSigned, setDpaSigned] = useState(false);
  const [optinKeywords, setOptinKeywords] = useState([]);
  const [optinMessage, setOptinMessage] = useState('');
  const [optinPopup, setOptinPopup] = useState(false);
  const [optoutKeywords, setOptoutKeywords] = useState([]);
  const [optoutMessage, setOptoutMessage] = useState('');
  const [optoutPopup, setOptoutPopup] = useState(false);
  const [helpKeywords, setHelpKeywords] = useState([]);
  const [helpMessage, setHelpMessage] = useState('');
  const [helpPopup, setHelpPopup] = useState(false);
  const [embeddedLinkPopup, setEmbeddedLinkPopup] = useState(false);
  const [isAcceptedByMe, setIsAcceptedByMe] = useState(false);
  const [provisional, setProvisional] = useState(null);
  const [mnoStatusInfo, setMnoStatusInfo] = useState(null);
  const [excludedMnos, setExcludedMnos] = useState([]);
  const [electCnpOptions, setElectCnpOptions] = useState([]);
  const eventTable = useTable(
    async ({ page = 1 }) => {
      const response = await getEventHistory({
        campaignUid,
        page,
      });
      return {
        data: response?.records || [],
        total: response?.totalRecords || 0,
        rowsPerPage: response?.recordsPerPage || 10,
        page: response?.page || 1,
      };
    },
    {
      deps: [campaignUid],
      inMemory: true,
      cappingTotal: MAX_EVENT_RECORDS,
    }
  );
  const isDeclined = sharingStatus === 'DECLINED';
  const rejectionCategoryText = (rejectionCategoryIds ?? [])
    .map(
      (rejectionCategoryId) =>
        rejectionCategoryMap[rejectionCategoryId] ?? rejectionCategoryId
    )
    .join(', ');

  const popupSetters = {
    optIn: setOptinPopup,
    optOut: setOptoutPopup,
    help: setHelpPopup,
    embeddedLink: setEmbeddedLinkPopup,
  };

  useEffect(() => {
    document.title = 'The Campaign Registry - Campaigns';
    initialize(campaignUid);
  }, []);

  useEffect(() => {
    updateProvisionalFlag(campaignUid);
    updateExcludedMnos(campaignUid);
    return () => {
      setCampaign({});
      setProvisional(null);
    };
  }, [campaignUid]);

  useEffect(() => {
    if (provisional != null) {
      updateProvisionalDependentData(campaignUid, provisional);
    }
  }, [provisional]);

  const updateProvisionalFlag = async (campaignUid) => {
    const result = await getMigrationAndProvisionalInfo(campaignUid);
    setProvisional(result.provisional);
    setIsAcceptedByMe(result.isAcceptedByMe);
  };

  const updateCampaignDetails = async (campaignUid, provisionalFlag) => {
    const data = await fetchCampaignDetails(campaignUid, {
      provisional: provisionalFlag,
    });
    if (data) {
      updateCnpOptions(data.campaign.usecase, provisionalFlag);
      setCampaign(data.campaign);
      setOptinKeywords(data.optinKeywords);
      setOptinMessage(data.optinMessage);
      setOptoutKeywords(data.optoutKeywords);
      setOptoutMessage(data.optoutMessage);
      setHelpKeywords(data.helpKeywords);
      setHelpMessage(data.helpMessage);
      setLoader(false);
    }
  };

  const updateElectedDca = (campaignUid, provisionalFlag) => {
    listCampaignElectedDcaApi(campaignUid, {
      provisional: provisionalFlag,
    }).then((electedDcasData) => {
      setElectedDcas(electedDcasData);
    });
  };

  const updateNominationStatus = (campaignUid, provisionalFlag) => {
    getNominationStatusApi(campaignUid, { provisional: provisionalFlag }).then(
      (data) => {
        setOpenAlert(!!data?.openAlert);
      }
    );
  };

  const updateSharingStatus = (campaignUid, provisional) => {
    campaignController
      .getCnpCampaigns({
        campaignUid,
        provisional,
      })
      .then((data) => {
        if (data) {
          const { records } = data;
          if (records.length > 0) {
            setSharingStatus(records[0].sharingStatus);
            setRejectionCategoryIds(
              records[0].sharedOnRejectionCategoryList || []
            );
            setExplanation(records[0].sharedOnExplanation || '');
            setDeclinedOnDate(records[0].sharedOnUpdateDate);
            setUpstreamCnpName(records[0].upstreamCnpName || '');
            setSharedToStatus(records[0].sharedToStatus || '');
          }
        }
      });
  };

  const updateExcludedMnos = async (campaignUid) => {
    const mnoIds = await campaignController.getExcludedMnos(campaignUid, {
      silent: true,
    });
    setExcludedMnos(mnoIds);
  };

  const updateProvisionalDependentData = async (
    campaignUid,
    provisionalFlag
  ) => {
    const mnoStatusInfo = await getCampaignOperations(
      campaignUid,
      {
        provisional: provisionalFlag,
      },
      {
        silent: true,
      }
    );
    setMnoStatusInfo(mnoStatusInfo);
    updateCampaignDetails(campaignUid, provisionalFlag);
    updateNominationStatus(campaignUid, provisionalFlag);
    updateElectedDca(campaignUid, provisionalFlag);
    updateSharingStatus(campaignUid, provisionalFlag);
  };

  const updateCnpOptions = async (useCase, provisional) => {
    const electCnps = await getCnpList({
      cnpMigrationSupported: provisional ? true : undefined,
      soleProprietorEnabled: useCase === 'SOLE_PROPRIETOR' ? true : undefined,
    });
    setElectCnpOptions(
      electCnps.map((cnp) => ({ label: cnp.displayName, value: cnp.uid }))
    );
  };

  const initialize = async (campaignUid) => {
    getCampaignAttachmentsApi(campaignUid, ATTACHMENT_FOLDER_MMS).then(
      (attachments) => {
        setSampleMultimedia(attachments);
      }
    );

    getCampaignTermsAndConditions().then((terms) => {
      setTermsAndConditions(terms);
    });

    getProfile().then((profile) => {
      setDpaSigned(profile.dpaSigned);
    });

    enumController.getRejectionCategories().then((records) => {
      setRejectionCategoryMap(
        Object.fromEntries(records.map((record) => [record.id, record.name]))
      );
    });
  };

  const handleGoBack = () => {
    history.push(`${goBackPage}`);
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
  };

  const handleMultimediaSelect = async (index) => {
    setSampleMultimediaIndex(index);
    if (!sampleMultimedia[index].file) {
      const response = await downloadAttachmentApi(
        sampleMultimedia[index].uuid
      );
      const updatedMultimedia = [...sampleMultimedia];
      updatedMultimedia.splice(index, 1, {
        ...sampleMultimedia[index],
        file: new File([response], sampleMultimedia[index].fileName, {
          type: sampleMultimedia[index].mimeType,
        }),
      });
      setSampleMultimedia(updatedMultimedia);
    }
  };

  const handleMultimediaDownload = async (index) => {
    await downloadAttachmentToDesktop(
      sampleMultimedia[index].uuid,
      sampleMultimedia[index].fileName
    );
  };

  const handlePopupChange = (name, active) => {
    if (active === true) {
      Object.keys(popupSetters)
        .filter((key) => key !== name)
        .forEach((key) => {
          popupSetters[key](false);
        });
    }

    const setter = popupSetters[name];
    if (setter) {
      setter(active);
    }
  };

  return (
    <section
      className="campaign-detail-section"
      data-testid="partnerCampaignDetail"
    >
      {loader ? (
        <Loader />
      ) : (
        <Container maxWidth={false} className="campaign-detail-container">
          <Grid container className="top-blk">
            <Grid item xs={6} className="back-btn">
              <a
                onClick={handleGoBack}
                className="paragraph"
                data-testid="partnerCampaignDetailBackButton"
              >
                <Flex>
                  <Box sx={{ mr: 'xs' }}>
                    <FontAwesomeIcon icon={faAngleLeft} color="#68737A" />
                  </Box>
                  {goBackPage.startsWith(CNP_CAMPAIGNS_PATH)
                    ? 'back to all campaigns'
                    : 'back'}
                </Flex>
              </a>
            </Grid>
          </Grid>
          <Grid container spacing={0}>
            <Grid item xs={12} data-testid="partnerCampaignDetailCampaignId">
              <div className="title">
                <h3 className="heading1">10DLC Campaign ID: {campaign.uid}</h3>
              </div>
            </Grid>
            <Grid item xs={12}>
              <PartnerCampaignDetailsBasic campaign={campaign} />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12}>
              {isDeclined ? (
                <Box sx={{ mt: 50, mb: 30 }}>
                  <DeclinedExplanation
                    rejectionCategory={rejectionCategoryText}
                    explanation={explanation}
                    date={declinedOnDate}
                  />
                </Box>
              ) : (
                <div
                  className="mno details"
                  data-testid="partnerCampaignDetailCarrierStatus"
                >
                  <HeadingBlock icon={faShareNodes} title="Carrier Status" />
                  <MnoStatusTableNew
                    mnoStatusInfo={mnoStatusInfo}
                    electedDcas={electedDcas}
                    excludedMnos={excludedMnos}
                    isCnp={true}
                  />
                </div>
              )}
            </Grid>
            <Grid item xs={12}>
              <div
                className="description details"
                data-testid="partnerCampaignDetailBrandDetails"
              >
                <HeadingBlock icon={faTag} title="Brand Details" />
                {campaign.brand && (
                  <BrandDetails
                    brand={campaign.brand}
                    usecase={campaign.usecase}
                    dpaSigned={dpaSigned}
                  />
                )}
              </div>
            </Grid>
            {isAcceptedByMe && !isDeclined && (
              <Grid item xs={12}>
                <NumbersProvisioned campaignUid={campaign.uid} />
              </Grid>
            )}
            <Grid item xs={12}>
              <div className="attributes details">
                <HeadingBlock
                  className="details-heading"
                  data-testid="partnerCampaignDetailCampaignAndContentAttributes"
                  icon={faSquarePollHorizontal}
                  title="Campaign and Content Attributes"
                />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    columnGap: 'l',
                    rowGap: 's',
                  }}
                >
                  <CampaignAttributeNew
                    type="view"
                    id="subscriberOptin"
                    value={campaign.subscriberOptin}
                    label="Subscriber Opt-in"
                    tooltip="Indicates whether the campaign is collecting and processing consumer opt-ins"
                    additionalSetup={
                      <MessageFlowAttributePopup
                        defaultReadonly
                        editable={false}
                        label="Opt-in"
                        keywordsTooltip="A keyword consumers can text to opt-in."
                        messageTooltip="If consumers can text in a keyword, the response should include the Brand DBA name, confirmation of opt-in enrollment to a recurring message campaign, how to get help, and clear description of how to opt-out."
                        keywords={optinKeywords}
                        message={optinMessage}
                        onClose={() => handlePopupChange('optIn', false)}
                      />
                    }
                    additionalSetupActive={optinPopup}
                    setAdditionalSetupActive={(active) =>
                      handlePopupChange('optIn', active)
                    }
                  />
                  <CampaignAttributeNew
                    type="view"
                    id="embeddedLink"
                    value={campaign.embeddedLink}
                    label="Embedded Link"
                    tooltip="Indicates whether the campaign is using an embedded link of any kind. Note that public URL shorteners (bitly, tinyurl) are not accepted"
                    additionalSetup={
                      <EmbeddedLinkPopup
                        editable={false}
                        defaultReadonly
                        value={campaign.embeddedLinkSample}
                        onClose={() => handlePopupChange('embeddedLink', false)}
                      />
                    }
                    additionalSetupActive={embeddedLinkPopup}
                    setAdditionalSetupActive={(active) =>
                      handlePopupChange('embeddedLink', active)
                    }
                  />
                  <CampaignAttributeNew
                    type="view"
                    id="subscriberOptout"
                    value={campaign.subscriberOptout}
                    label="Subscriber Opt-Out"
                    tooltip="Indicates whether the campaign is collecting and processing consumer opt-outs"
                    additionalSetup={
                      <MessageFlowAttributePopup
                        defaultReadonly
                        editable={false}
                        label="Opt-out"
                        keywordsTooltip="A keyword consumers can text to opt-out."
                        messageTooltip="The response to the STOP keyword may include the Brand DBA name but should include an acknowledgement of opt-out request and confirmation that no further messages will be sent."
                        keywords={optoutKeywords}
                        message={optoutMessage}
                        onClose={() => handlePopupChange('optOut', false)}
                      />
                    }
                    additionalSetupActive={optoutPopup}
                    setAdditionalSetupActive={(active) =>
                      handlePopupChange('optOut', active)
                    }
                  />
                  <CampaignAttributeNew
                    type="view"
                    id="embeddedPhone"
                    value={campaign.embeddedPhone}
                    label="Embedded Phone Number"
                    tooltip="Indicates whether the campaign is using an embedded phone number (except the required HELP information contact phone number)"
                  />
                  <CampaignAttributeNew
                    type="view"
                    id="subscriberHelp"
                    value={campaign.subscriberHelp}
                    label="Subscriber Help"
                    tooltip="Indicates whether the campaign has implemented message reply providing customers on how they can contact the message sender after they reply with the “HELP” keyword."
                    additionalSetup={
                      <MessageFlowAttributePopup
                        defaultReadonly
                        editable={false}
                        label="Help"
                        keywordsTooltip="A keyword consumers use to get help or more information."
                        messageTooltip="The response to HELP keyword may include the Brand DBA name and additional support contact information."
                        keywords={helpKeywords}
                        message={helpMessage}
                        onClose={() => handlePopupChange('help', false)}
                      />
                    }
                    additionalSetupActive={helpPopup}
                    setAdditionalSetupActive={(active) =>
                      handlePopupChange('help', active)
                    }
                  />
                  <CampaignAttributeNew
                    type="view"
                    id="ageGated"
                    value={campaign.ageGated}
                    label="Age-Gated Content"
                    tooltip="Indicates whether the campaign include any age-gated content as defined by Carrier and CTIA guidelines"
                  />
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    columnGap: 'l',
                    mt: 's',
                  }}
                >
                  <Flex sx={{ flexDirection: 'column', gap: 's' }}>
                    <CampaignAttributeNew
                      type="view"
                      id="numberPool"
                      value={campaign.numberPool}
                      label="Number Pooling"
                      tooltip="Indicates whether more than 50 numbers will be used for the campaign."
                    />
                    <CampaignAttributeNew
                      type="view"
                      id="directLending"
                      value={campaign.directLending}
                      label="Direct Lending or Loan Arrangement"
                      tooltip="Indicates whether the campaign include content related to direct lending or other loan arrangements"
                    />
                  </Flex>
                  <CampaignAttributeNew
                    type="view"
                    label="Terms & Conditions"
                    tooltip="Indicates the campaign follows CTIA messaging principles and best practices"
                    notes={termsAndConditions}
                    id="termsAndConditions"
                    value={campaign.termsAndConditions}
                  />
                </Box>
              </div>
            </Grid>
            <Grid item xs={12}>
              <div
                className="description details"
                data-testid="partnerCampaignDetailCampaignDescription"
              >
                <HeadingBlock icon={faBullhorn} title="Campaign Description" />
                <CampaignDescription
                  description={campaign.description}
                  messageFlow={campaign.messageFlow}
                  privacyPolicyLink={campaign.privacyPolicyLink}
                  termsAndConditionsLink={campaign.termsAndConditionsLink}
                />
              </div>
            </Grid>
            <SupportingDocumentSection readonly campaignUid={campaignUid} />
            <Grid item xs={12}>
              <div
                className="sample-messages details"
                data-testid="partnerCampaignDetailSampleMessages"
              >
                <HeadingBlock icon={faMessageDots} title="Sample Messages" />
                {Object.keys(campaign).map((k, index) => {
                  if (k.includes('sample')) {
                    return (
                      campaign[k] && (
                        <CampaignSampleMessage
                          key={index}
                          index={k.slice(-1)}
                          message={campaign[k]}
                        />
                      )
                    );
                  }
                })}
              </div>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <div
              className="sample-messages details"
              data-testid="partnerCampaignDetailSamplMultimedia"
            >
              <HeadingBlock icon={faFileImage} title="Sample Multimedia" />
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <MultimediaList
                    data-testid="campaignDetailSampleMultimediaFiles"
                    title="Sample Multimedia Files"
                    loading={
                      sampleMultimediaIndex > -1 &&
                      !sampleMultimedia[sampleMultimediaIndex].file
                    }
                    attachments={sampleMultimedia}
                    onSelect={handleMultimediaSelect}
                    onDownload={handleMultimediaDownload}
                    style={{ height: 275 }}
                  />
                </Grid>
                {sampleMultimediaIndex > -1 &&
                  sampleMultimedia[sampleMultimediaIndex].file && (
                    <AttachmentPreview
                      onClose={() => setSampleMultimediaIndex(-1)}
                      file={sampleMultimedia[sampleMultimediaIndex].file}
                      mimeType={
                        sampleMultimedia[sampleMultimediaIndex].mimeType
                      }
                      data-testid="partnerCampaignDetailSamplMultimediaBackdrop"
                    />
                  )}
              </Grid>
            </div>
            {!isDeclined && (
              <>
                <OtherResponsiblePartiesSection
                  variant="cnp"
                  campaign={campaign}
                  nominationInfo={{
                    status: sharedToStatus,
                    upstreamCnpName,
                  }}
                  electCnpOptions={electCnpOptions}
                  onCnpChange={(success) => {
                    if (success) {
                      updateSharingStatus(campaignUid, provisional);
                    }
                  }}
                />
                <Grid item xs={12}>
                  <div
                    className="campaign-events details"
                    data-testid="partnerCampaignDetailEvent"
                  >
                    <HeadingBlock icon={faCalendarRange} title="Events" />
                    <Grid container>
                      <Table
                        headRows={headRows}
                        emptyState="no events to view"
                        className="campaign-detail-events-table"
                        disableHover
                        loading={eventTable.loading}
                        tableData={eventTable.tableData.map((record, index) => (
                          <EventListingRow key={index} event={record} />
                        ))}
                        handleChangePage={eventTable.handleChangePage}
                        pagination={eventTable.pagination}
                      />
                    </Grid>
                  </div>
                </Grid>
              </>
            )}
          </Grid>
          {!isDeclined && (
            <ElectCNPAlert handleClose={handleCloseAlert} open={openAlert} />
          )}
        </Container>
      )}
    </section>
  );
};

export default withStyles(useStyles)(PartnerCampaignDetailV2);
